/**
 * useShoppingCart Hook
 * 
 * Custom React hook that provides a simplified interface for cart operations.
 * Acts as a bridge between components and the ShoppingCartContext.
 * 
 * Features:
 * - Simplified cart operations (add, remove, update, clear)
 * - Cart state access with computed properties
 * - Loading and error state management
 * - Shopify synchronization helpers
 * - Type-safe operations with proper error handling
 * - Optimistic updates with rollback on failure
 */

import { useCallback, useMemo } from 'react';
import { useShoppingCartContext } from '../contexts/ShoppingCartContext';
import { ShopifyProduct, ShopifyProductVariant } from '../types/shopify';
import { debugWarn } from '../utils/debugLog';

// Hook return interface
export interface UseShoppingCartReturn {
  // Cart state
  items: ReturnType<typeof useShoppingCartContext>['state']['items'];
  totals: ReturnType<typeof useShoppingCartContext>['state']['totals'];
  isLoading: boolean;
  error: string | null;
  isEmpty: boolean;
  itemCount: number;
  uniqueItemCount: number;

  // Cart operations
  addItem: (
    product: ShopifyProduct,
    variant: ShopifyProductVariant,
    quantity?: number,
    selectedOptions?: Record<string, string>
  ) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;

  // Item queries
  getItemQuantity: (variantId: string, selectedOptions?: Record<string, string>) => number;
  isItemInCart: (variantId: string, selectedOptions?: Record<string, string>) => boolean;
  getItemById: (itemId: string) => ReturnType<typeof useShoppingCartContext>['state']['items'][0] | undefined;

  // Shopify integration
  syncWithShopify: () => Promise<void>;
  getCheckoutUrl: () => string | null;

  // Utility functions
  canAddItem: (variant: ShopifyProductVariant, quantity?: number) => boolean;
  getVariantInCart: (variantId: string, selectedOptions?: Record<string, string>) => ReturnType<typeof useShoppingCartContext>['state']['items'][0] | undefined;
  getTotalWeight: () => number;
  getEstimatedShipping: () => number;
  
  // Cart validation
  hasOutOfStockItems: () => boolean;
  getOutOfStockItems: () => ReturnType<typeof useShoppingCartContext>['state']['items'];
  validateCart: () => { isValid: boolean; errors: string[] };
}

/**
 * Custom hook for shopping cart operations
 * 
 * Provides a simplified interface for interacting with the shopping cart,
 * including state access, operations, and utility functions.
 * 
 * @returns UseShoppingCartReturn - Cart state and operations
 */
export const useShoppingCart = (): UseShoppingCartReturn => {
  const {
    state,
    addItem: contextAddItem,
    removeItem: contextRemoveItem,
    updateQuantity: contextUpdateQuantity,
    clearCart: contextClearCart,
    getItemQuantity: contextGetItemQuantity,
    isItemInCart: contextIsItemInCart,
    syncWithShopify: contextSyncWithShopify,
    getCheckoutUrl: contextGetCheckoutUrl,
  } = useShoppingCartContext();

  // Memoized computed properties
  const isEmpty = useMemo(() => state.items.length === 0, [state.items.length]);
  const itemCount = useMemo(() => state.totals.itemCount, [state.totals.itemCount]);
  const uniqueItemCount = useMemo(() => state.totals.uniqueItemCount, [state.totals.uniqueItemCount]);

  // Cart operations with enhanced functionality
  const addItem = useCallback(
    (
      product: ShopifyProduct,
      variant: ShopifyProductVariant,
      quantity: number = 1,
      selectedOptions: Record<string, string> = {}
    ) => {
      // Validate inputs
      if (quantity <= 0) {
        debugWarn('Cannot add item with quantity <= 0');
        return;
      }

      if (!variant.availableForSale) {
        debugWarn('Cannot add unavailable item to cart');
        return;
      }

      // Check stock availability
      if (variant.quantityAvailable !== null && variant.quantityAvailable !== undefined) {
        const currentQuantity = contextGetItemQuantity(variant.id, selectedOptions);
        const totalQuantity = currentQuantity + quantity;
        const availableToAdd = variant.quantityAvailable - currentQuantity;
        
        if (totalQuantity > variant.quantityAvailable) {
          debugWarn(`Cannot add ${quantity} items. Only ${availableToAdd} available (${currentQuantity} already in cart, ${variant.quantityAvailable} total stock).`);
          return;
        }
      }

      contextAddItem(product, variant, quantity, selectedOptions);
    },
    [contextAddItem, contextGetItemQuantity]
  );

  const removeItem = useCallback(
    (itemId: string) => {
      contextRemoveItem(itemId);
    },
    [contextRemoveItem]
  );

  const updateQuantity = useCallback(
    (itemId: string, quantity: number) => {
      if (quantity < 0) {
        debugWarn('Cannot set negative quantity');
        return;
      }

      // Find the item to validate stock
      const item = state.items.find(item => item.id === itemId);
      if (item && quantity > 0) {
        const { variant } = item;
        if (variant.quantityAvailable !== null && variant.quantityAvailable !== undefined) {
          if (quantity > variant.quantityAvailable) {
            debugWarn(`Cannot set quantity to ${quantity}. Only ${variant.quantityAvailable} available.`);
            return;
          }
        }
      }

      contextUpdateQuantity(itemId, quantity);
    },
    [contextUpdateQuantity, state.items]
  );

  const clearCart = useCallback(() => {
    contextClearCart();
  }, [contextClearCart]);

  // Item query functions
  const getItemQuantity = useCallback(
    (variantId: string, selectedOptions: Record<string, string> = {}) => {
      return contextGetItemQuantity(variantId, selectedOptions);
    },
    [contextGetItemQuantity]
  );

  const isItemInCart = useCallback(
    (variantId: string, selectedOptions: Record<string, string> = {}) => {
      return contextIsItemInCart(variantId, selectedOptions);
    },
    [contextIsItemInCart]
  );

  const getItemById = useCallback(
    (itemId: string) => {
      return state.items.find(item => item.id === itemId);
    },
    [state.items]
  );

  // Shopify integration
  const syncWithShopify = useCallback(async () => {
    await contextSyncWithShopify();
  }, [contextSyncWithShopify]);

  const getCheckoutUrl = useCallback(() => {
    return contextGetCheckoutUrl();
  }, [contextGetCheckoutUrl]);

  // Utility functions
  const canAddItem = useCallback(
    (variant: ShopifyProductVariant, quantity: number = 1) => {
      if (!variant.availableForSale) {
        return false;
      }

      if (variant.quantityAvailable !== null && variant.quantityAvailable !== undefined) {
        const currentQuantity = contextGetItemQuantity(variant.id);
        return (currentQuantity + quantity) <= variant.quantityAvailable;
      }

      return true;
    },
    [contextGetItemQuantity]
  );

  const getVariantInCart = useCallback(
    (variantId: string, selectedOptions: Record<string, string> = {}) => {
      // Generate the same ID format as used in the context
      const optionsString = Object.keys(selectedOptions)
        .sort()
        .map(key => `${key}:${selectedOptions[key]}`)
        .join('|');
      const itemId = `${variantId}${optionsString ? `_${btoa(optionsString)}` : ''}`;
      
      return state.items.find(item => item.id === itemId);
    },
    [state.items]
  );

  const getTotalWeight = useCallback(() => {
    // Calculate total weight if weight data is available
    // This would require weight information in the product data
    return 0; // Placeholder - would need weight data from Shopify
  }, []);

  const getEstimatedShipping = useCallback(() => {
    return state.totals.shipping;
  }, [state.totals.shipping]);

  // Cart validation functions
  const hasOutOfStockItems = useCallback(() => {
    return state.items.some(item => !item.variant.availableForSale);
  }, [state.items]);

  const getOutOfStockItems = useCallback(() => {
    return state.items.filter(item => !item.variant.availableForSale);
  }, [state.items]);

  const validateCart = useCallback(() => {
    const errors: string[] = [];
    let isValid = true;

    // Check for out of stock items
    const outOfStockItems = getOutOfStockItems();
    if (outOfStockItems.length > 0) {
      isValid = false;
      errors.push(`${outOfStockItems.length} item(s) are out of stock`);
    }

    // Check for quantity availability issues
    state.items.forEach(item => {
      const { variant, quantity } = item;
      if (variant.quantityAvailable !== null && variant.quantityAvailable !== undefined) {
        if (quantity > variant.quantityAvailable) {
          isValid = false;
          errors.push(`${item.product.title} - Only ${variant.quantityAvailable} available, but ${quantity} in cart`);
        }
      }
    });

    // Check for empty cart
    if (state.items.length === 0) {
      isValid = false;
      errors.push('Cart is empty');
    }

    return { isValid, errors };
  }, [state.items, getOutOfStockItems]);

  return {
    // Cart state
    items: state.items,
    totals: state.totals,
    isLoading: state.isLoading,
    error: state.error,
    isEmpty,
    itemCount,
    uniqueItemCount,

    // Cart operations
    addItem,
    removeItem,
    updateQuantity,
    clearCart,

    // Item queries
    getItemQuantity,
    isItemInCart,
    getItemById,

    // Shopify integration
    syncWithShopify,
    getCheckoutUrl,

    // Utility functions
    canAddItem,
    getVariantInCart,
    getTotalWeight,
    getEstimatedShipping,

    // Cart validation
    hasOutOfStockItems,
    getOutOfStockItems,
    validateCart,
  };
};

export default useShoppingCart;
